
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { User, Phone, Mail, MapPin, Users } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface Contact {
  name: string;
  phone: string;
  email?: string;
}

interface Address {
  street: string;
  street2?: string;
  city: string;
  province: string;
  postal_code: string;
  country: string;
}

interface BookingContactCardProps {
  type: "Collection" | "Delivery";
  contacts: Contact[];
  address: Address | null;
}

const BookingContactCard = ({
  type,
  contacts,
  address
}: BookingContactCardProps) => {
  const isMobile = useIsMobile();
  const safeContacts = contacts || [];

  const handlePhoneClick = (phoneNumber: string) => {
    window.open(`tel:${phoneNumber}`, '_self');
  };

  const handleEmailClick = (email: string) => {
    window.open(`mailto:${email}`, '_self');
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="truncate">{type} Details</span>
          <Badge variant="outline" className="ml-auto text-xs flex-shrink-0">
            {type === "Collection" ? "From" : "To"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Contact Information */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-1">
            <Users className="h-3 w-3 flex-shrink-0" />
            Contacts ({safeContacts.length})
          </h4>
          {safeContacts.length > 0 ? (
            <div className="space-y-3">
              {safeContacts.map((contact, index) => (
                <div key={index} className="space-y-2 p-3 border rounded-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm font-medium truncate">
                      {contact.name}
                      {index === 0 && <span className="text-xs text-primary ml-1 whitespace-nowrap">(Primary)</span>}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handlePhoneClick(contact.phone)} 
                      className="h-8 px-3 text-xs justify-start min-w-0 w-full"
                    >
                      <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{contact.phone}</span>
                    </Button>
                    {contact.email && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => handleEmailClick(contact.email!)} 
                        className="h-8 px-3 text-xs justify-start min-w-0 w-full"
                      >
                        <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{contact.email}</span>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No contact information</p>
          )}
        </div>

        {/* Address Information */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-muted-foreground">Address</h4>
          {address ? (
            <address className="not-italic text-sm leading-relaxed break-words">
              <div className="font-medium break-words">{address.street}</div>
              {address.street2 && <div className="break-words">{address.street2}</div>}
              <div className="break-words">{address.city}, {address.province}</div>
              <div className="break-words">{address.postal_code}</div>
              <div className="text-muted-foreground break-words">{address.country}</div>
            </address>
          ) : (
            <p className="text-sm text-muted-foreground">No address provided</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingContactCard;
